import { z } from 'zod';
import { COURSE_CATEGORIES } from '../constants/categories.js';
import { SUB_CATEGORIES, subCategoryOverrideKey } from '../constants/sub-categories.js';

/** Max length of a custom category / sub-category label. */
export const LABEL_MAX_LENGTH = 60;

/** True if the string contains any C0 control char, tab/newline, or DEL (0x7F). */
function hasControlChars(s: string): boolean {
  for (let i = 0; i < s.length; i++) {
    const code = s.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f) return true;
  }
  return false;
}

/**
 * A single label value. Trimmed first; the empty string is allowed and signals
 * "reset to default" (the service drops the key rather than storing `""`).
 */
export const LabelValueSchema = z
  .string()
  .transform((s) => s.trim())
  .refine((s) => s.length <= LABEL_MAX_LENGTH, `Label must be ${LABEL_MAX_LENGTH} characters or fewer`)
  .refine((s) => !hasControlChars(s), 'Label contains invalid characters');

/**
 * Category label overrides, keyed by the four locked category enum values.
 * `.strict()` rejects any key that is not a real category.
 */
export const CategoryLabelsSchema = z
  .object({
    vocational: LabelValueSchema.optional(),
    higher_education: LabelValueSchema.optional(),
    research: LabelValueSchema.optional(),
    trade: LabelValueSchema.optional(),
  })
  .strict();

/** The set of valid `${category}.${slug}` sub-category override keys. */
const ALLOWED_SUB_KEYS = new Set<string>(
  COURSE_CATEGORIES.flatMap((cat) =>
    SUB_CATEGORIES[cat].map((sub) => subCategoryOverrideKey(cat, sub.slug)),
  ),
);

/**
 * Sub-category label overrides, keyed by `${category}.${slug}`. Any key not in
 * the locked SUB_CATEGORIES set is rejected, so arbitrary keys can't enter the jsonb.
 */
export const SubCategoryLabelsSchema = z.record(z.string(), LabelValueSchema).superRefine((obj, ctx) => {
  for (const key of Object.keys(obj)) {
    if (!ALLOWED_SUB_KEYS.has(key)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Unknown sub-category key: ${key}`,
        path: [key],
      });
    }
  }
});

/**
 * Body of PATCH /api/v1/admin/categories. Each provided map fully replaces that
 * column's overrides (after trimming + dropping empties), so clearing a field
 * resets it to the system default. `.strict()` blocks any other tenant column.
 */
export const UpdateCategoryLabelsSchema = z
  .object({
    category_labels: CategoryLabelsSchema.optional(),
    sub_category_labels: SubCategoryLabelsSchema.optional(),
  })
  .strict();

export type UpdateCategoryLabelsInput = z.infer<typeof UpdateCategoryLabelsSchema>;
